import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Attribute, NumericChange } from 'src/app/models/product-models/views/Attribute';
import { AttributeValue } from 'src/app/models/product-models/views/AttributeValue';
import { LocalizedText } from 'src/app/models/product-models/views/LocalizedText';
import { AttributeMap, AttributePair, Product } from 'src/app/models/product-models/views/Product';
import { recursivelyBuildDefaultChildrenMap } from 'src/app/models/utils';
import { AttributeValueOutput } from '../attribute-value/attribute-value.component';
import { NumericAttributeTypeEnum } from 'src/app/models/interfaces/GenericInterfaces';
import { ReferenceControllerService } from '../services/reference-controller.service';

enum AttributeSelectionMode {
  Defaults = 'Defaults',
  Leaves = 'Leaves',
}
const NUMERIC_ATTRIBUTE_TYPE_SELECTOR = 'type';

export const HIDDEN_WITHOUT_REFERENCES = 'hiddenWithoutReferences';

@Component({
  selector: 'app-attribute',
  templateUrl: './attribute.component.html',
  styleUrls: ['./attribute.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttributeComponent {
  private _mode = AttributeSelectionMode.Defaults;
  @Input() attributes?: Attribute[];
  @Input() attributeSettings?: AttributeMap;
  @Input() product?: Product;
  @Input() showClose = false;
  @Input() showTitle = true;
  @Input() showInfo = false;
  @Input() immidiatelySetAttribute = false;

  @Output() attributeSet: EventEmitter<AttributeMap> = new EventEmitter<AttributeMap>();
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();
  @Output() attributeWasSetImmidiately = new EventEmitter<AttributePair>();

  constructor(
    private translateService: TranslateService,
    private referenceControllerService: ReferenceControllerService,
  ) {}

  public isNumericChange(attribute: Attribute) {
    if (attribute instanceof NumericChange) {
      return attribute as NumericChange;
    } else {
      return null;
    }
  }

  public toURL(url: string) {
    return 'url(' + url + ')';
  }

  public hideAttributeHeader(attribute: Attribute) {
    return attribute.displayParameters?.settings?.hideHeader;
  }

  public showAttribute(attribute: Attribute) {
    if (!attribute.isShown) return false;
    if (attribute.displayParameters && attribute.displayParameters[HIDDEN_WITHOUT_REFERENCES] === true) {
      for (const attributeValue of attribute.attributeValues) {
        if (this.referenceControllerService.checkIfAttributeAndValueReferenceAnything(attribute, attributeValue)) {
          return true;
        }
      }
      return false;
    }
    return true;
  }

  public attributeValueClick(data: AttributeValueOutput) {
    const attribute = data.attribute;
    const attributeValue = data.attributeValue;

    if (!attribute || !attributeValue) return;

    if (attributeValue.attributes.length === 0) {
      if (this.immidiatelySetAttribute) {
        this.product?.setAttribute(attribute, attributeValue);
        this.attributeWasSetImmidiately.next({ attribute: attribute, attributeValue: attributeValue });

        return;
      }
      const attributeMap = new Map(this.attributeSettings);
      attributeMap.set(attribute, {
        attributeValue: attributeValue,
        map: undefined,
      });

      this.attributeSet.emit(attributeMap);
    } else if (this._mode === AttributeSelectionMode.Defaults) {
      const map = recursivelyBuildDefaultChildrenMap(attributeValue.attributes);

      if (this.immidiatelySetAttribute && map) {
        this.product?.setAttribute(attribute, attributeValue);
        this.attributeWasSetImmidiately.next({ attribute: attribute, attributeValue: attributeValue });
        this._recursivelySetBranchAttributes(map);
        return;
      }
      const attributeMap = new Map(this.attributeSettings);
      attributeMap.set(attribute, {
        attributeValue: attributeValue,
        map: map,
      });
      this.attributeSet.emit(attributeMap);
    }
  }

  private _recursivelySetBranchAttributes(attributeMap: AttributeMap) {
    for (const [attribute, attributeSetting] of attributeMap) {
      if (!attribute || !attributeSetting) continue;
      this.product?.setAttribute(attribute, attributeSetting?.attributeValue);
      if (attributeSetting.map) this._recursivelySetBranchAttributes(attributeSetting?.map);
    }
  }

  public childClick(attribute: Attribute, value: AttributeMap) {
    if (!this.attributeSettings) {
      throw new Error('Attribute settings are missing when user tried to set attribute value');
    }

    const attributeMap: AttributeMap = new Map(this.attributeSettings);
    const foundAttribute = this.attributeSettings.get(attribute);

    if (this.attributeSettings && foundAttribute) {
      attributeMap.set(attribute, {
        attributeValue: foundAttribute.attributeValue,
        map: value,
      });
    }

    this.attributeSet.emit(attributeMap);
  }

  public onNumericAttributeChange(attribute: Attribute, value: number) {
    const attributeMap = new Map(this.attributeSettings);
    attributeMap.set(attribute, { attributeValue: value, map: undefined });
    if (this.immidiatelySetAttribute) {
      this.product?.setAttribute(attribute, value);
      this.attributeWasSetImmidiately.next({ attribute: attribute, attributeValue: value });
      return;
    } else this.attributeSet.emit(attributeMap);
  }

  public getNumericAttributeType(attribute: Attribute) {
    if (
      attribute.builderData &&
      attribute.builderData[NUMERIC_ATTRIBUTE_TYPE_SELECTOR] === NumericAttributeTypeEnum.SELECT
    )
      return NumericAttributeTypeEnum.SELECT;
    return NumericAttributeTypeEnum.INPUT;
  }

  public get NumericAttributeTypeEnum() {
    return NumericAttributeTypeEnum;
  }

  public isSet(attribute: Attribute, attributeValue: AttributeValue) {
    return this.attributeSettings?.get(attribute)?.attributeValue === attributeValue;
  }

  public isNumericSet(attribute: Attribute) {
    const attributeValue = this.attributeSettings?.get(attribute)?.attributeValue;
    if (typeof attributeValue === 'number') return attributeValue;
    return undefined;
  }

  public attributeValueHasAttributes(attribute: Attribute) {
    const attributeValue = this.attributeSettings?.get(attribute)?.attributeValue;
    if (attributeValue instanceof AttributeValue && attributeValue.attributes) return attributeValue;
    return undefined;
  }

  public closeClick() {
    this.closed.next();
  }

  getLocalizedString(texts: LocalizedText[]): string | null {
    if (!texts) return null;
    if (typeof texts === 'string') return texts;

    const text = texts.find((text) => text.language === this.translateService.currentLang);
    return text ? text.text : null;
  }
}
